import { Formik, Form, FormikProps } from 'formik';
import React, {
  FC,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { Scope1DesiredData } from 'models/ActivityDataScope1DesiredData/scope1DesiredData.model';
import { ScopeData } from 'models/ActivityDataEntryScopes/scopes.model';
import { HelpDeskTooltipContext } from 'context/HelpDeskTooltipContext';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { LocationData } from 'models/ActivityDataEntryScopes/locationData.model';
import { useParams } from 'react-router-dom';
import useFilters from 'shared/hooks/useFilters';
import { queryParamsList } from 'views/ActivityDataEntry';
import { transformItem } from 'shared/utils/convertScopeValues';
import SwitchToMonthlyModal from '../SwitchToMonthlyModal';
import SwitchAlertOfMonthlyDataModal from '../SwitchAlertOfMonthlyDataModal';
import { scopeOneValidationSchema } from './validationSchema';
import { fugitiveEmissions } from 'constants/constants';
import { ProcessingFacilityTableProps } from 'enums/ProcessingFacilityEnums';
import { BreakDownDropdownOptionsEnums } from 'enums/BreakDownDropdownOptions';
import { customValidate } from './scopeOneUtils';
import { handleAlertSubmit as handleAlertSubmitUtil } from './scopeOneUtils';
import ScopeOneCategoryFields from './ScopeOneCategoryFields';
import DashboardService from 'services/DashboardService/dashboard.service';
import Stage1AndStage2ProductService from 'services/ProductsService/stage1AndStage2Product.service';
import { convertToOptions } from 'shared/utils/convertOptions';

export const naturalGasUnitOptions = [
  {
    label: 'm3',
    value: 'm3',
  },
  { label: 'GJ', value: 'GJ' },
];

interface ScopeOneFormComponentProps {
  formikRef?: React.RefObject<FormikProps<Scope1DesiredData>>;
  activeYear?: string;
  activeScope?: string;
  scope1Details?: Scope1DesiredData;
  locationScopeDetails?: LocationData;
  createScopeForLocation?: (
    locationId?: string,
    year?: string,
    scopeCategory?: string,
    payload?: ScopeData
  ) => Promise<void>;
  fetchYearForLocation?: (locationId?: string, year?: string) => Promise<void>;
  createDesiredScopeData?: (locationScopeDetails?: LocationData) => void;
  setIsEdit?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ScopeOneFormComponent: FC<ScopeOneFormComponentProps> = ({
  formikRef,
  activeYear,
  activeScope,
  scope1Details,
  createScopeForLocation,
  fetchYearForLocation,
  setIsEdit,
}) => {
  const { fetchIngredientOrigin } = Stage1AndStage2ProductService();
  const { fetchActivityMapping, activityMapping } = DashboardService();
  const { openTooltipModal } = useContext(HelpDeskTooltipContext);
  const { getParams, setParams } = useFilters();
  const [isSwitchMonthly, setIsSwitchMonthly] = useState(false);
  const [switchMonthlyItem, setSwitchMonthlyItem] = useState(new ScopeData());
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedSourceIndex, setSelectedSourceIndex] = useState('');
  const [isSwitchMonthlyAlert, setIsSwitchMonthlyAlert] = useState(false);
  const [initialMonthlyValues, setInitialMonthlyValues] = useState(
    new ScopeData()
  );
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>();

  const { isFirstFlow, isEditView } = getParams(queryParamsList);

  const { id: locationId } = useParams();

  const initialValues: Scope1DesiredData = useMemo(() => {
    return {
      ...(scope1Details
        ? {
            ...scope1Details,
            fugitiveEmissions: scope1Details?.fugitiveEmissions?.length
              ? scope1Details?.fugitiveEmissions
              : [
                  {
                    ...new ScopeData(),
                    fuelName: HelpDeskInputFieldEnum.refrigerantFieldName,
                    scopeCategory: ProcessingFacilityTableProps.SCOPE1_INDEX,
                    scopeFieldCategory: fugitiveEmissions,
                    unit: BreakDownDropdownOptionsEnums.KG,
                    fuelValue: null,
                  },
                ],
          }
        : {}),
    };
  }, [scope1Details]);

  useEffect(() => {
    fetchActivityMapping({
      activity_label: HelpDeskInputFieldEnum.refrigerant,
    });
  }, []);

  // Will add for new fields once its seeded in the DB
  const handleToolTipOpen = (fieldName?: HelpDeskInputFieldEnum) => () =>
    fieldName && openTooltipModal(fieldName);

  const refrigerantOptions = activityMapping?.map(item => ({
    label: item.activityLabel,
    value: item.activityLabel,
  }));

  const handleSubmit = async (values: Scope1DesiredData) => {
    const {
      stationaryCombustion,
      mobileSources,
      processSources,
      fugitiveEmissions,
    } = values;
    const cleanedData = fugitiveEmissions?.filter(
      item =>
        item?.fuelValue ||
        item?.refrigerantLabel ||
        item?.refrigerantRegionSpecId
    );
    const combinedItems = [
      ...(stationaryCombustion ?? []),
      ...(mobileSources ?? []),
      ...(processSources ?? []),
      ...(cleanedData ?? []),
    ];

    // Flatten and transform both arrays
    const combinedItemsFlattend = combinedItems?.map(transformItem);
    const desiredPayload = [...(combinedItemsFlattend as ScopeData[])];
    await createScopeForLocation?.(
      locationId,
      activeYear,
      activeScope,
      desiredPayload as ScopeData
    );
    setIsEdit?.(false);
    setParams(
      isFirstFlow
        ? { activeTab: activeYear, isFirstFlow: true }
        : { activeTab: activeYear },
      undefined,
      true
    );
    await fetchYearForLocation?.(locationId, activeYear);
  };

  const handleSwitch = useCallback(
    (
        source?: ScopeData,
        key?: string,
        selectedIndex?: string,
        selectedItemIndex?: number
      ) =>
      () => {
        if (source?.fuelValue) {
          setIsSwitchMonthlyAlert?.(true);
        } else {
          setIsSwitchMonthly(true);
        }
        const values = formikRef?.current?.values;
        const sourceItem = values?.[key as keyof Scope1DesiredData];
        const selectedItem = sourceItem?.filter(
          item => source?.id === item?.id
        );
        let resultedValue;
        if (selectedItem && selectedItem?.length > 1) {
          const refrigirentItem = sourceItem?.filter(
            (_, index) => index === selectedItemIndex
          );
          resultedValue = refrigirentItem?.[0];
        } else {
          resultedValue = selectedItem?.[0];
        }
        const desiredInitialValues = {
          ...resultedValue,
          scopeMonthlyData: !resultedValue?.isYearly
            ? resultedValue?.scopeMonthlyData
            : undefined,
        };
        setInitialMonthlyValues(desiredInitialValues);
        formikRef?.current?.setFieldValue(
          `${selectedIndex}`,
          desiredInitialValues
        );
        setSelectedItemIndex(selectedItemIndex);
        source && setSwitchMonthlyItem(source);
        key && setSelectedSource(key);
        selectedIndex && setSelectedSourceIndex(selectedIndex);
      },
    [selectedSourceIndex]
  );

  const handleMonthlyModalOpen = useCallback(
    (
        source?: ScopeData,
        key?: string,
        selectedIndex?: string,
        selectedItemIndex?: number
      ) =>
      () => {
        source && setSwitchMonthlyItem(source);
        key && setSelectedSource(key);
        selectedIndex && setSelectedSourceIndex(selectedIndex);
        const values = formikRef?.current?.values;
        const sourceItem = values?.[key as keyof Scope1DesiredData];
        const selectedItem = sourceItem?.filter(
          item => source?.id === item?.id
        );
        let resultedValue;
        if (selectedItem && selectedItem?.length > 1) {
          const refrigirentItem = sourceItem?.filter(
            (_, index) => index === selectedItemIndex
          );
          resultedValue = refrigirentItem?.[0];
        } else {
          resultedValue = selectedItem?.[0];
        }
        const desiredInitialValues = {
          ...resultedValue,
        };
        setInitialMonthlyValues(desiredInitialValues);
        setIsSwitchMonthly(true);
      },
    [selectedSourceIndex]
  );

  const handleModalSubmit = (values?: ScopeData) => {
    const totalValue = values?.scopeMonthlyData?.reduce((acc, item) => {
      return acc + (item?.value ? Number(item?.value) : 0);
    }, 0);
    formikRef?.current?.setFieldValue?.(`${selectedSourceIndex}`, {
      ...values,
      isYearly: false,
      fuelValue: Number.isInteger(totalValue)
        ? totalValue
        : totalValue?.toFixed(4).replace(/\.?0+$/, ''),
    });
    setIsSwitchMonthly(false);
  };
  const handleAlertSubmit = () => {
    handleAlertSubmitUtil(
      formikRef,
      selectedSource,
      switchMonthlyItem,
      selectedItemIndex,
      selectedSourceIndex,
      setIsSwitchMonthly,
      setIsSwitchMonthlyAlert,
      setInitialMonthlyValues
    );
  };

  const {
    stationaryCombustion,
    mobileSources,
    fugitiveEmissions: refrigirentKey,
    processSources,
  } = HelpDeskInputFieldEnum;
  return (
    <Formik
      initialValues={initialValues}
      innerRef={formikRef}
      validationSchema={scopeOneValidationSchema}
      enableReinitialize
      validate={customValidate}
      onSubmit={handleSubmit}>
      <Form>
        <ScopeOneCategoryFields
          stationaryCombustion={stationaryCombustion}
          mobileSources={mobileSources}
          processSources={processSources}
          refrigirentKey={refrigirentKey}
          handleToolTipOpen={handleToolTipOpen}
          isEditView={isEditView}
          handleMonthlyModalOpen={handleMonthlyModalOpen}
          handleSwitch={handleSwitch}
          refrigerantOptions={refrigerantOptions}
          fetchIngredientOrigin={fetchIngredientOrigin}
        />
        <SwitchToMonthlyModal
          isModalOpen={isSwitchMonthly}
          setIsModalOpen={setIsSwitchMonthly}
          initialValues={initialMonthlyValues}
          handleSubmit={handleModalSubmit}
        />
        <SwitchAlertOfMonthlyDataModal
          isModalOpen={isSwitchMonthlyAlert}
          setIsModalOpen={setIsSwitchMonthlyAlert}
          handleSubmit={handleAlertSubmit}
          sourceValue={switchMonthlyItem}
        />
      </Form>
    </Formik>
  );
};

export default ScopeOneFormComponent;
